import React, { useEffect, useState } from 'react';
import { ClockIcon } from 'lucide-react';
import { DAILY_CAMPAIGN } from '../config/site';
interface WarsawNow {
  dateLabel: string;
  secondsUntilCutoff: number;
}
function cutoffSeconds(): number {
  const [h, m, s] = DAILY_CAMPAIGN.cutoff.split(':').map(part => Number(part));
  return h * 3600 + m * 60 + s;
}

/** Aktualny czas w strefie Europe/Warsaw, niezależnie od strefy przeglądarki. */
function readWarsawNow(): WarsawNow {
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: DAILY_CAMPAIGN.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? '0');
  const hour = get('hour') % 24;
  const elapsed = hour * 3600 + get('minute') * 60 + get('second');
  const day = String(get('day')).padStart(2, '0');
  const month = String(get('month')).padStart(2, '0');
  return {
    dateLabel: `${day}.${month}.${get('year')}`,
    secondsUntilCutoff: Math.max(0, cutoffSeconds() - elapsed)
  };
}
function TimeUnit({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="flex min-w-[52px] flex-col items-center" data-id="element-144" data-mp="3jfo58b42ua6f">
      <span className="font-display text-3xl font-bold leading-none text-white tabular-nums sm:text-4xl" data-id="element-145" data-mp="3jg7jnhuiqgfp">
        {String(value).padStart(2, '0')}
      </span>
      <span className="mt-1.5 text-[10px] font-semibold tracking-[0.14em] text-white/55" data-id="element-146" data-mp="f1zgsqkjpn9w">
        {label}
      </span>
    </div>;
}
export function DailyCountdown() {
  const [now, setNow] = useState<WarsawNow>(readWarsawNow);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(readWarsawNow()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  if (!DAILY_CAMPAIGN.enabled || now.secondsUntilCutoff <= 0) return null;
  const hours = Math.floor(now.secondsUntilCutoff / 3600);
  const minutes = Math.floor(now.secondsUntilCutoff % 3600 / 60);
  const seconds = now.secondsUntilCutoff % 60;
  return <aside aria-label="Czas pozostały do końca dzisiejszej oferty" className="mt-4 rounded-2xl border border-chem/60 bg-navy px-5 py-5 text-white shadow-[0_18px_34px_-22px_rgba(11,29,58,0.9)]" data-id="element-147" data-mp="13nnjhvgsn3vw">
      <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:gap-6 sm:text-left" data-id="element-148" data-mp="tnd9ejq22cky">
        <div className="min-w-0" data-id="element-149" data-mp="pnw53tmd7lhd">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-chem/60 bg-chem/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white" data-id="element-150" data-mp="kw4j7we5m3xk">
            <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-151" data-mp="fufawoibg4d6" />
            Oferta na dzisiaj
          </span>
          {DAILY_CAMPAIGN.dateLabel && <p className="mt-2 text-xs font-semibold tracking-[0.16em] text-white/60" data-id="element-152" data-mp="5xivrzxwwjzn">
              {now.dateLabel}
            </p>}
          <p className="mt-2 text-sm font-semibold leading-snug text-white" data-id="element-153" data-mp="1xs8w7biao0e">
            Dzisiejsza oferta kończy się o północy.
          </p>
          <p className="mt-1 text-sm leading-snug text-white/70" data-id="element-154" data-mp="2hlnujum8yrg1">
            Zabezpiecz dostęp do Wizualnej Chemii™ zanim zakończy się bieżąca kampania.
          </p>
        </div>

        <div className="flex shrink-0 items-start gap-2" role="timer" aria-live="off" data-id="element-155" data-mp="2rk3ijopz9klb">
          <TimeUnit value={hours} label="GODZ." data-id="element-156" data-mp="2rhr4hmncbudj" />
          <span className="font-display text-2xl font-bold text-chem" aria-hidden="true" data-id="element-157" data-mp="1txeb2zciafs2">
            :
          </span>
          <TimeUnit value={minutes} label="MIN." data-id="element-158" data-mp="1yvza4vg0utex" />
          <span className="font-display text-2xl font-bold text-chem" aria-hidden="true" data-id="element-159" data-mp="23uspyuis6wi9">
            :
          </span>
          <TimeUnit value={seconds} label="SEK." data-id="element-160" data-mp="28liyggmi7fh6" />
        </div>
      </div>
    </aside>;
}